import React, { Component } from "react";
import axios from "axios";
import { Navigate } from "react-router-dom";

import Constants from "Helpers/Constants";
import init from "Helpers/WindowToken";
import {
  apiErrorMessage,
  firstError,
  hasErrors,
  slugify,
  validateBrand,
} from "Helpers/ContentValidation";
import { brandFormToFormData, emptyBrandForm } from "Helpers/ContentPayloads";

import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";

import { Card } from "@mui/material";

import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDSnackbar from "components/MDSnackbar";
import MDTypography from "components/MDTypography";

import BrandFormFields from "components/ContentForms/BrandFormFields";

export default class AddBrand extends Component {
  state = {
    values: emptyBrandForm(),
    errors: {},
    parentOptions: [],
    // The URL key follows the name until the admin edits it directly.
    slugTouched: false,

    loading: false,
    notification: false,
    notiColor: "success",
    message: "",
    redirect: false,
  };

  componentDidMount() {
    this.fetchParentOptions();
  }

  /* =============================
     PARENT BRAND OPTIONS
  ============================= */
  fetchParentOptions = async () => {
    if (init() !== "success") return this.setState({ redirect: true });

    try {
      const response = await axios.get(Constants.getUrls.getBrandOptions);
      // Only top-level brands can be a parent — the API rejects deeper nesting.
      const options = (response.data?.data || []).filter(
        (option) => !option.parent_brand
      );
      this.setState({ parentOptions: options });
    } catch (error) {
      this.notify("warning", apiErrorMessage(error, "Could not load brands"));
    }
  };

  /* =============================
     FORM STATE
  ============================= */
  handleChange = (name, value) => {
    this.setState((state) => {
      const values = { ...state.values, [name]: value };

      // Keep the URL key in step with the name until the admin edits it.
      if (name === "name" && !state.slugTouched) {
        values.slug = slugify(value);
      }

      // Clearing the field that was flagged clears its message.
      const errors = { ...state.errors };
      delete errors[name];

      return { values, errors, slugTouched: name === "slug" ? true : state.slugTouched };
    });
  };

  handleFile = (file) => {
    this.setState((state) => ({
      values: { ...state.values, logo_file: file },
      errors: { ...state.errors, logo: undefined },
    }));
  };

  notify = (notiColor, message) => {
    this.setState({ notification: true, notiColor, message });
    setTimeout(() => this.setState({ notification: false, message: "" }), 4000);
  };

  /* =============================
     SUBMIT
  ============================= */
  handleSubmit = async (event) => {
    event.preventDefault();

    const errors = validateBrand(this.state.values, { isUpdate: false });
    if (hasErrors(errors)) {
      this.setState({ errors });
      return this.notify("warning", firstError(errors));
    }

    if (init() !== "success") return this.setState({ redirect: true });

    this.setState({ loading: true, errors: {} });

    try {
      const response = await axios.post(
        Constants.postUrls.createBrand,
        brandFormToFormData(this.state.values)
      );

      this.setState({ loading: false });
      this.notify("success", response.data?.message || "Brand created successfully!");
      setTimeout(() => this.setState({ redirect: true }), 1200);
    } catch (error) {
      this.setState({ loading: false });
      this.notify("warning", apiErrorMessage(error, "Could not create the brand"));
    }
  };

  render() {
    if (this.state.redirect) return <Navigate to="/brandpages" replace />;

    return (
      <DashboardLayout>
        <DashboardNavbar />

        <MDBox pt={6} pb={3}>
          <Card>
            <MDBox
              mx={2}
              mt={-3}
              py={3}
              px={2}
              variant="gradient"
              bgColor="info"
              borderRadius="lg"
              coloredShadow="info"
            >
              <MDTypography variant="h6" color="white">
                Add Brand Page
              </MDTypography>
            </MDBox>

            <MDBox py={3} px={2}>
              <form onSubmit={this.handleSubmit} noValidate>
                <BrandFormFields
                  values={this.state.values}
                  errors={this.state.errors}
                  onChange={this.handleChange}
                  onFile={this.handleFile}
                  parentOptions={this.state.parentOptions}
                />

                <MDBox mt={4} display="flex" gap={2}>
                  <MDButton
                    type="submit"
                    variant="gradient"
                    color="info"
                    disabled={this.state.loading}
                  >
                    {this.state.loading ? "Saving..." : "Create Brand"}
                  </MDButton>

                  <MDButton
                    variant="outlined"
                    color="dark"
                    onClick={() => this.setState({ redirect: true })}
                    disabled={this.state.loading}
                  >
                    Cancel
                  </MDButton>
                </MDBox>
              </form>
            </MDBox>

            <MDSnackbar
              color={this.state.notiColor}
              icon="notifications"
              title="Brand Pages"
              content={this.state.message}
              dateTime="Just now"
              open={this.state.notification}
              close={() => this.setState({ notification: false })}
            />
          </Card>
        </MDBox>
      </DashboardLayout>
    );
  }
}
